import dayjs from 'dayjs';
import { useMemo } from 'react';
import { StringParam, useQueryParam, withDefault } from 'use-query-params';
import { DEFAULT_GROUP_BY_INDEX, groupByOptions } from './consts';
const getDaysDifference = (date1, date2) => {
    return dayjs(date2).diff(dayjs(date1), 'day');
};
const getAvailableGroupByOptions = (dateRange) => {
    if (!dateRange.dates || dateRange.dates.length < 2) {
        return groupByOptions;
    }
    const [fromDate, toDate] = dateRange.dates;
    const daysDifference = getDaysDifference(fromDate, toDate);
    if (daysDifference <= 30) {
        // For range <= 30 days: hour, day
        return groupByOptions.filter((option) => ['hour', 'day'].includes(option.value));
    }
    if (daysDifference <= 90) {
        // For range > 30 days but <= 90 days: day only
        return groupByOptions.filter((option) => option.value === 'day');
    }
    // For range > 90 days: day, month
    return groupByOptions.filter((option) => ['day', 'month'].includes(option.value));
};
export const useGroupByParams = (dateRange) => {
    const [groupByParam, setGroupByParam] = useQueryParam('groupBy', withDefault(StringParam, undefined));
    const availableOptions = useMemo(() => {
        return getAvailableGroupByOptions(dateRange);
    }, [dateRange]);
    const groupBy = useMemo(() => {
        // If no groupBy parameter, set default option from available options
        if (!groupByParam) {
            return availableOptions[DEFAULT_GROUP_BY_INDEX] || availableOptions[0];
        }
        // Check if the groupBy value is valid and available for current date range
        const isValidAndAvailable = availableOptions.some((option) => option.value === groupByParam);
        if (!isValidAndAvailable) {
            // If not available, return first available option as fallback
            return availableOptions[0];
        }
        // Find the matching option
        const matchingOption = availableOptions.find((option) => option.value === groupByParam);
        if (matchingOption) {
            // If it matches a predefined option, return it
            return matchingOption;
        }
        // This should never happen due to validation above, but return first available as fallback
        return availableOptions[0];
    }, [groupByParam, availableOptions]);
    const options = useMemo(() => {
        return availableOptions;
    }, [availableOptions]);
    const setGroupBy = (newGroupBy) => {
        setGroupByParam(newGroupBy.value);
    };
    return {
        options,
        groupBy,
        setGroupBy,
    };
};
